import { useMemo } from "react";
import { CheckCircle2, Clock3, HandCoins, HeartHandshake, ListFilter, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

const money = (value) => `$${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

const categoryFor = (reg) => {
  if (reg.is_sponsored) return "sponsored";
  if ((reg.total_amount || 0) > 0 && (reg.paid_amount || 0) >= (reg.total_amount || 0) - 0.01) return "full";
  if ((reg.paid_amount || 0) > 0.01) return "partial";
  return "credit";
};

const CATEGORY_META = {
  full: { label: "Pagaron completo", icon: CheckCircle2, tone: "border-emerald-200 bg-emerald-50 text-emerald-900" },
  partial: { label: "Abonaron", icon: HandCoins, tone: "border-amber-200 bg-amber-50 text-amber-900" },
  credit: { label: "Crédito sin abono", icon: Clock3, tone: "border-rose-200 bg-rose-50 text-rose-900" },
  sponsored: { label: "Becados", icon: HeartHandshake, tone: "border-sky-200 bg-sky-50 text-sky-900" },
};

export const AccountingOverview = ({ stats, regs, onOpenReports, onOpenCleanup }) => {
  const accounting = stats?.accounting;
  const groups = useMemo(() => {
    const result = { full: [], partial: [], credit: [], sponsored: [] };
    [...regs]
      .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || "", "es", { sensitivity: "base" }))
      .forEach((reg) => result[categoryFor(reg)].push(reg));
    return result;
  }, [regs]);

  if (!accounting) return null;
  const global = accounting.global;

  return (
    <section className="mb-8" data-testid="accounting-overview">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-gray-500 font-bold">Contabilidad verificada</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 className="font-heading text-3xl text-black">Balance por persona</h2>
            <span className="text-sm font-bold text-gray-500" data-testid="stat-total-registrations">
              {stats.total_registrations} inscritos · {stats.adults} adultos · {stats.children} niños
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onOpenCleanup} data-testid="accounting-open-cleanup">
            <AlertTriangle className="w-4 h-4 mr-2" /> Duplicados y sin asignar
          </Button>
          <Button onClick={onOpenReports} data-testid="accounting-open-reports" className="bg-black text-white hover:bg-gray-800">
            <ListFilter className="w-4 h-4 mr-2" /> PDF / CSV
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        {Object.entries(CATEGORY_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const bucket = accounting.categories[key];
          const primary = key === "sponsored" ? bucket.covered : bucket.collected;
          return (
            <div key={key} className={`border rounded-lg p-4 ${meta.tone}`} data-testid={`accounting-card-${key}`}>
              <Icon className="w-5 h-5 mb-3" />
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{meta.label}</div>
              <div className="font-heading text-3xl" data-testid={`accounting-count-${key}`}>{bucket.people}</div>
              <div className="text-xs mt-1" data-testid={`accounting-amount-${key}`}>
                {key === "credit" ? `${money(bucket.pending)} por cobrar` : key === "sponsored" ? `${money(primary)} cubierto` : `${money(primary)} recibido`}
              </div>
              {key === "partial" && <div className="text-xs font-bold mt-1">{money(bucket.pending)} pendiente</div>}
            </div>
          );
        })}
      </div>

      <div className="bg-black text-white border-l-8 border-yellow-400 p-5 mb-4" data-testid="accounting-global-balance">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <BalanceValue label="Cobrado real" value={global.collected_real} testid="balance-collected-real" />
          <BalanceValue label="Cubierto por beca" value={global.sponsored_covered} testid="balance-sponsored" />
          <BalanceValue label="Cubierto total" value={global.covered_total} testid="balance-covered-total" />
          <BalanceValue label="Pendiente real" value={global.pending_real} testid="balance-pending-real" highlight />
          <BalanceValue label="Total esperado" value={global.expected} testid="balance-expected" />
        </div>
      </div>

      <Tabs defaultValue="partial" className="bg-white border border-gray-200 rounded-lg p-3" data-testid="accounting-lists">
        <TabsList className="w-full h-auto flex flex-wrap justify-start">
          {Object.entries(CATEGORY_META).map(([key, meta]) => (
            <TabsTrigger key={key} value={key} data-testid={`accounting-tab-${key}`}>
              {meta.label} ({groups[key].length})
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.keys(CATEGORY_META).map((key) => (
          <TabsContent key={key} value={key}>
            <AccountingList category={key} people={groups[key]} />
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
};

const BalanceValue = ({ label, value, testid, highlight }) => (
  <div>
    <div className="text-[9px] uppercase tracking-widest text-white/55 font-bold">{label}</div>
    <div className={`font-heading text-2xl ${highlight ? "text-yellow-400" : "text-white"}`} data-testid={testid}>{money(value)}</div>
  </div>
);

const AccountingList = ({ category, people }) => (
  <div className="max-h-72 overflow-y-auto divide-y divide-gray-100" data-testid={`accounting-list-${category}`}>
    {people.length === 0 ? (
      <div className="py-8 text-center text-sm text-gray-400">No hay personas en esta categoría.</div>
    ) : people.map((person) => (
      <div key={person.id} className="py-2.5 px-1 flex items-center justify-between gap-4" data-testid={`accounting-person-${person.id}`}>
        <div className="min-w-0">
          <div className="font-semibold text-sm text-gray-900 truncate">{person.full_name}</div>
          <div className="text-[10px] text-gray-500">{person.camper_number_display || "Sin número"}</div>
        </div>
        <div className="text-right flex-shrink-0 text-xs">
          {category === "sponsored" ? (
            <><b className="text-sky-700">{money(person.sponsor_amount || person.total_amount)}</b><div className="text-gray-400">beca</div></>
          ) : category === "credit" ? (
            <><b className="text-rose-700">{money(person.remaining_amount)}</b><div className="text-gray-400">por cobrar</div></>
          ) : (
            <><b className="text-emerald-700">{money(person.paid_amount)}</b><div className="text-gray-400">{category === "partial" ? `${money(person.remaining_amount)} pendiente` : "completo"}</div></>
          )}
        </div>
      </div>
    ))}
  </div>
);